import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerTrigger,
  DrawerClose,
} from "@/components/ui/drawer";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { AddonItem, PropertyItem } from "@/data/addonsData";

type AddOnsCheckoutDrawerProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedAddon: AddonItem | undefined;
  selectedPropertyId: string | null;
  properties: PropertyItem[];
  subtotal: number;
  birthdayColor: string;
  setBirthdayColor: (value: string) => void;
  isSubmitting: boolean;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  checkInDate?: Date;
  setCheckInDate: (date?: Date) => void;
  checkOutDate?: Date;
  setCheckOutDate: (date?: Date) => void;
};

export const AddOnsCheckoutDrawer = ({
  isOpen,
  onOpenChange,
  selectedAddon,
  selectedPropertyId,
  properties,
  subtotal,
  isSubmitting,
  onSubmit,
  checkInDate,
  setCheckInDate,
  checkOutDate,
  setCheckOutDate,
}: AddOnsCheckoutDrawerProps) => {
  const canCheckout = selectedPropertyId && selectedAddon;

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <Button
          disabled={!canCheckout}
          className="flex-1 h-14 rounded-xl text-black font-bold text-lg shadow-[0_0_20px_rgba(255,236,0,0.3)] hover:bg-primary/90 disabled:opacity-50 disabled:shadow-none"
        >
          {canCheckout ? (
            <>
              Checkout <ChevronRight className="w-5 h-5 ml-1" />
            </>
          ) : (
            "Select Property & Add-On"
          )}
        </Button>
      </DrawerTrigger>
      <DrawerContent className="bg-card border-white/10">
        <div className="max-w-md mx-auto w-full">
          <DrawerHeader className="text-left">
            <DrawerTitle className="text-2xl">Complete Request</DrawerTitle>
            <DrawerDescription>
              Enter your reservation details to add these to your stay.
            </DrawerDescription>
          </DrawerHeader>

          <form onSubmit={onSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  required
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  required
                  className="bg-background"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  className="bg-background"
                  placeholder="(555) 555-5555"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 flex flex-col">
                <Label>Check-in</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal bg-background h-10",
                        !checkInDate && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {checkInDate ? (
                        format(checkInDate, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={checkInDate}
                      onSelect={setCheckInDate}
                      initialFocus
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                    />
                  </PopoverContent>
                </Popover>
                <input
                  type="hidden"
                  name="checkIn"
                  value={checkInDate ? format(checkInDate, "yyyy-MM-dd") : ""}
                  required
                />
              </div>
              <div className="space-y-2 flex flex-col">
                <Label>Check-out</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal bg-background h-10",
                        !checkOutDate && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {checkOutDate ? (
                        format(checkOutDate, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={checkOutDate}
                      onSelect={setCheckOutDate}
                      initialFocus
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                    />
                  </PopoverContent>
                </Popover>
                <input
                  type="hidden"
                  name="checkOut"
                  value={checkOutDate ? format(checkOutDate, "yyyy-MM-dd") : ""}
                  required
                />
              </div>
            </div>

            <DrawerFooter className="px-0 pt-6">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 text-black font-bold text-lg"
              >
                {isSubmitting
                  ? "Processing..."
                  : `Confirm • $${subtotal.toFixed(2)}`}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline" className="w-full h-12">
                  Cancel
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </form>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
